/**
 * W1-11a (app-registry-keymap): focus regions (ARCHITECTURE.md §6.3, DESIGN.md §8.1-8.2).
 *
 * - Region cycling: `next(1 | -1)` moves focus to the next/previous visible panel region (`[data-region]`, leaf
 *   regions in document order; a container region such as the dock is a stop only when it holds no visible inner
 *   region). Focus returns to the element last focused inside that region, else the region root (made focusable
 *   with tabindex=-1). Bound by the shell module through `focusRegionCommands` / `FOCUS_REGION_KEYBINDINGS`
 *   (Ctrl+Backquote / Ctrl+Shift+Backquote; macOS: Alt+Backquote / Alt+Shift+Backquote, since Cmd+` cycles windows).
 * - Chrome buttons do not take focus on pointer down: mousedown on a button-like control inside `[data-chrome]` or
 *   `[role="toolbar"]` is prevented (text fields and selects there still take focus).
 * - Pointer focus hand-back: when a chrome control gains focus from the pointer anyway (a menu returning focus to
 *   its trigger), focus goes back to the last editing region, so Space and the editing keys keep working.
 * - Input modality (`modality`, `focusedByKeyboard`): the single tracker of the `:focus-visible` rule. It listens on
 *   the window in the capture phase, so keys the KeyboardRouter routes (and stops) still count as keyboard use; the
 *   router is given this controller (`focus` dep) and asks it rather than keeping a second opinion.
 * - Region atoms (`activeRegionsOf`, used by the KeyboardRouter for `focus.<region>` / `hover.<region>`): the closest
 *   `[data-region]` decides (ARCHITECTURE.md §6.1). The dock is the one container region: `dock` also holds inside
 *   its tabs (lines, problems, history, assets, raw). Any other nesting (e.g. a curve editor inside a motion column)
 *   reports only the innermost region, so panel atoms never hold together and equal-specificity bindings of two
 *   panels can never both apply.
 *
 * App wiring (one import): main.tsx calls `focusRegions.install()` at startup (`installKeyboardRouter` also installs
 * it and shares it as the router's `focus` dep); the shell module (W1-11b, owner of the `view.*` ids) adds
 * `FOCUS_REGION_COMMANDS` to its commands and `FOCUS_REGION_KEYBINDINGS` to its keybindings.
 */

import type { CommandDef, KeyBinding, KeymapPresetId } from '../registry/types';
import { isBareModifier } from '../registry/chords';
import { m } from '../registry/registry.messages';
import { DATA_REGIONS, DOCK_TABS } from '../registry/when';

/** Regions in which the user edits (focus hand-back targets): every data-region value. */
export const EDITING_REGIONS: ReadonlySet<string> = new Set(DATA_REGIONS);

const CHROME_CONTAINER = '[data-chrome], [role="toolbar"], [role="menubar"]';
const BUTTON_LIKE =
  'button, a[href], summary, [role="button"], [role="tab"], [role="radio"], [role="checkbox"], [role="switch"], ' +
  '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="option"], ' +
  'input[type="checkbox"], input[type="radio"], input[type="button"], input[type="submit"], input[type="reset"]';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/** Is this element (and its ancestors) rendered? Layout-free so it also works in tests. */
export function isElementVisible(el: Element): boolean {
  for (let cur: Element | null = el; cur !== null; cur = cur.parentElement) {
    if (cur.hasAttribute('hidden') || cur.getAttribute('aria-hidden') === 'true' || cur.hasAttribute('inert')) {
      return false;
    }
    const view = cur.ownerDocument.defaultView;
    if (view !== null) {
      const style = view.getComputedStyle(cur);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
  }
  return true;
}

/** The dock's tabs: inside the dock, `focus.dock` holds together with these (and only these). */
export const DOCK_TAB_REGIONS: ReadonlySet<string> = new Set(DOCK_TABS);

/** data-region values containing `el`, innermost first (every enclosing region; see `activeRegionsOf` for atoms). */
export function regionsOf(el: Element | null): string[] {
  const out: string[] = [];
  for (
    let cur = el?.closest('[data-region]') ?? null;
    cur !== null;
    cur = cur.parentElement?.closest('[data-region]') ?? null
  ) {
    const r = cur.getAttribute('data-region');
    if (r !== null && r !== '') out.push(r);
  }
  return out;
}

/**
 * Regions whose `focus.` / `hover.` atoms hold for `el`: the closest region, plus `dock` when that region is the
 * dock or one of its tabs inside the dock.
 */
export function activeRegionsOf(el: Element | null): string[] {
  const all = regionsOf(el);
  const inner = all[0];
  if (inner === undefined) return [];
  if (inner !== 'dock' && DOCK_TAB_REGIONS.has(inner) && all.includes('dock')) return [inner, 'dock'];
  return [inner];
}

/** True when `el` is a button-like control inside the app chrome (top bar, tool rail, toolbars). */
export function isChromeControl(el: Element | null): boolean {
  if (el === null) return false;
  const control = el.closest(BUTTON_LIKE);
  return control !== null && control.closest(CHROME_CONTAINER) !== null;
}

export interface FocusRegionsOptions {
  readonly document?: Document;
}

export interface FocusRegions {
  /** Visible region stops in document order. */
  stops(): HTMLElement[];
  /** Focus the next (1) or previous (-1) region; returns the region name, or null when there is none. */
  next(dir: 1 | -1): string | null;
  /** Focus a region by name (its last focused element, else its root); false when it is not visible. */
  focus(region: string): boolean;
  /** Region the user last edited in (null before any). */
  lastRegion(): string | null;
  /** Return focus to the last editing region (after pointer use of chrome); false when there is none. */
  handBack(): boolean;
  /** Was the currently focused element focused by keyboard (Tab, arrows, region cycling)? */
  focusedByKeyboard(el?: Element | null): boolean;
  /** Current input modality: the last non-modifier key press, pointer press or region move decides. */
  modality(): 'keyboard' | 'pointer';
  /**
   * Start listening (window, capture phase, so routed keys the KeyboardRouter stops still count as keyboard use);
   * returns the stop function (idempotent).
   */
  install(): () => void;
  dispose(): void;
}

export function createFocusRegions(opts: FocusRegionsOptions = {}): FocusRegions {
  // Resolved on use, so the app singleton below can be created where no DOM exists (workers, node tests).
  let resolvedDoc: Document | null = opts.document ?? null;
  const getDoc = (): Document => (resolvedDoc ??= document);
  /** Last focused element per region element. */
  const lastInRegion = new WeakMap<Element, Element>();
  let lastRegionEl: HTMLElement | null = null;
  let lastEditing: Element | null = null;
  let modality: 'keyboard' | 'pointer' = 'pointer';
  const keyboardFocused = new WeakSet<Element>();
  let installed: (() => void) | null = null;

  const focusEl = (el: HTMLElement): void => {
    el.focus({ preventScroll: true });
  };

  const stops = (): HTMLElement[] => {
    const all = [...getDoc().querySelectorAll<HTMLElement>('[data-region]')].filter(isElementVisible);
    return all.filter((r) => !all.some((o) => o !== r && r.contains(o)));
  };

  const focusRegionElement = (root: HTMLElement): void => {
    const remembered = lastInRegion.get(root);
    if (
      remembered instanceof HTMLElement &&
      remembered.isConnected &&
      root.contains(remembered) &&
      isElementVisible(remembered)
    ) {
      focusEl(remembered);
      return;
    }
    if (!root.hasAttribute('tabindex') && !root.matches(FOCUSABLE)) root.setAttribute('tabindex', '-1');
    focusEl(root);
  };

  const onFocusIn = (e: FocusEvent): void => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (modality === 'keyboard') keyboardFocused.add(target);
    else keyboardFocused.delete(target);
    const regionEl = target.closest<HTMLElement>('[data-region]');
    if (regionEl !== null) {
      for (
        let r: HTMLElement | null = regionEl;
        r !== null;
        r = r.parentElement?.closest<HTMLElement>('[data-region]') ?? null
      ) {
        lastInRegion.set(r, target);
      }
      const name = regionEl.getAttribute('data-region') ?? '';
      if (EDITING_REGIONS.has(name) && !isChromeControl(target)) {
        lastRegionEl = regionEl;
        lastEditing = target;
      }
      return;
    }
    // A chrome control focused by the pointer (e.g. a menu returning focus to its trigger): hand focus back.
    if (modality === 'pointer' && isChromeControl(target)) {
      queueMicrotask(() => {
        if (getDoc().activeElement !== target) return;
        if (!handBack() && target instanceof HTMLElement) target.blur();
      });
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isBareModifier(e)) modality = 'keyboard';
  };

  const onPointerDown = (): void => {
    modality = 'pointer';
  };

  const onMouseDown = (e: MouseEvent): void => {
    const target = e.target;
    if (!(target instanceof Element) || e.button !== 0) return;
    if (isChromeControl(target)) e.preventDefault();
  };

  const handBack = (): boolean => {
    if (lastEditing instanceof HTMLElement && lastEditing.isConnected && isElementVisible(lastEditing)) {
      focusEl(lastEditing);
      return true;
    }
    if (lastRegionEl?.isConnected === true && isElementVisible(lastRegionEl)) {
      focusRegionElement(lastRegionEl);
      return true;
    }
    return false;
  };

  const api: FocusRegions = {
    stops,
    next(dir) {
      const list = stops();
      if (list.length === 0) return null;
      const active = getDoc().activeElement;
      const current = list.findIndex((r) => active !== null && r.contains(active));
      const from = current >= 0 ? current : dir === 1 ? -1 : list.length;
      const target = list[(from + dir + list.length) % list.length];
      if (target === undefined) return null;
      modality = 'keyboard';
      focusRegionElement(target);
      return target.getAttribute('data-region');
    },
    focus(region) {
      const root =
        stops().find((r) => r.getAttribute('data-region') === region) ??
        [...getDoc().querySelectorAll<HTMLElement>(`[data-region="${CSS.escape(region)}"]`)].find(isElementVisible);
      if (root === undefined) return false;
      modality = 'keyboard';
      focusRegionElement(root);
      return true;
    },
    lastRegion: () => lastRegionEl?.getAttribute('data-region') ?? null,
    handBack,
    focusedByKeyboard(el = getDoc().activeElement) {
      return el !== null && keyboardFocused.has(el);
    },
    modality: () => modality,
    install() {
      if (installed !== null) return installed;
      const doc = getDoc();
      // The window is the first capture target: the KeyboardRouter's window listener stops routed keys there, but
      // stopPropagation never skips other listeners of the same target, so every key press is seen here too.
      const target: Window | Document = doc.defaultView ?? doc;
      target.addEventListener('focusin', onFocusIn as EventListener, true);
      target.addEventListener('keydown', onKeyDown as EventListener, true);
      target.addEventListener('pointerdown', onPointerDown, true);
      target.addEventListener('mousedown', onMouseDown as EventListener, true);
      const dispose = (): void => {
        target.removeEventListener('focusin', onFocusIn as EventListener, true);
        target.removeEventListener('keydown', onKeyDown as EventListener, true);
        target.removeEventListener('pointerdown', onPointerDown, true);
        target.removeEventListener('mousedown', onMouseDown as EventListener, true);
        installed = null;
      };
      installed = dispose;
      return dispose;
    },
    dispose() {
      installed?.();
    },
  };
  return api;
}

// ---------------------------------------------------------------------------------------------------------------
// Commands and bindings for the shell module (W1-11b registers them; ids are in its `view.*` namespace)
// ---------------------------------------------------------------------------------------------------------------

export const FOCUS_NEXT_REGION = 'view.focusNextRegion';
export const FOCUS_PREVIOUS_REGION = 'view.focusPreviousRegion';

/** Region cycling commands over a FocusRegions controller (menu/palette paths: View menu, palette). */
export function focusRegionCommands(regions: () => FocusRegions): CommandDef[] {
  return [
    {
      id: FOCUS_NEXT_REGION,
      title: m.focusNextRegion,
      category: 'View',
      icon: 'PanelsTopLeft',
      aliases: ['focus', 'panel', 'region', 'F6'],
      level: 'guided',
      hint: 10,
      run: () => {
        regions().next(1);
      },
      menus: [{ menu: 'app.view', group: 'focus', order: 10 }],
    },
    {
      id: FOCUS_PREVIOUS_REGION,
      title: m.focusPreviousRegion,
      category: 'View',
      icon: 'PanelsTopLeft',
      aliases: ['focus', 'panel', 'region'],
      level: 'guided',
      run: () => {
        regions().next(-1);
      },
      menus: [{ menu: 'app.view', group: 'focus', order: 11 }],
    },
  ];
}

const REGION_BINDINGS: readonly KeyBinding[] = [
  { chord: 'Ctrl+Backquote', command: FOCUS_NEXT_REGION, mac: 'Alt+Backquote' },
  { chord: 'Ctrl+Shift+Backquote', command: FOCUS_PREVIOUS_REGION, mac: 'Alt+Shift+Backquote' },
];

/** Bindings for both presets (DESIGN.md §8.2; the RPE preset keeps them, its Tab is the line info overlay). */
export const FOCUS_REGION_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: REGION_BINDINGS,
  rpe: REGION_BINDINGS,
};

// ---------------------------------------------------------------------------------------------------------------
// App wiring
// ---------------------------------------------------------------------------------------------------------------

/** The app's focus regions controller (main.tsx: `focusRegions.install()` once at startup). */
export const focusRegions: FocusRegions = createFocusRegions();

/** Region cycling commands over the app controller, ready for the shell module's `commands`. */
export const FOCUS_REGION_COMMANDS: readonly CommandDef[] = focusRegionCommands(() => focusRegions);
